// Export of a client's uploads per month and document type (ClientService.GetClientUploadsBreakdown)
// as an Xtram PDF or an Excel file.
import * as XLSX from 'xlsx-js-style';
import { buildReportWorkbook } from './ExcelReport';
import { beautifyNumber } from './FormatNumber';
import EnumsService from '../Services/enums.service';
import { XTRAM_LOGOS } from '../assests/branding/xtram/logos';
import { AGING_REPORT_COLORS as C } from './receivablesAgingExport';

const COUNT_KEYS = ['expenses', 'incomes', 'paymentAcceptances', 'otherDocuments', 'billableUploads', 'processedPages'];

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const safeFileName = (value) => String(value || 'Client').replace(/[\\/:*?"<>|]/g, '_');

/** "2026-09" → "09/2026". */
export const formatMonthKey = (monthKey) => {
    const [y, m] = String(monthKey || '').split('-');
    return y && m ? `${m}/${y}` : '';
};

/** Upload month label; months counted from live rows (no usage row) get a "*". */
export const formatUploadMonth = (month) => {
    const label = formatMonthKey(month?.monthKey);
    return month?.fromUsageTable === false ? `${label} *` : label;
};

const SPLIT_KEYS = ['expenses', 'incomes', 'paymentAcceptances', 'otherDocuments', 'total'];

const splitHeaders = (t) => [
    t('UploadMonth'), t('ReportingMonth'), t('Expenses'), t('Incomes'),
    t('PaymentAcceptances'), t('OtherDocuments'), t('Total'),
];

/** One row per (upload month, reporting month), newest upload month first. */
const splitRows = (data) =>
    [...(data?.months || [])].reverse().flatMap((m) =>
        (m.savedInReportingMonths || []).map((r) => [
            formatUploadMonth(m), formatMonthKey(r.monthKey), ...SPLIT_KEYS.map((k) => Number(r[k]) || 0),
        ]));

/** Per-month reporting split as lines, e.g. ["08/2026: 257", "09/2026: 194"]. */
const splitLines = (month) =>
    (month?.savedInReportingMonths || []).map((r) => `${formatMonthKey(r.monthKey)}: ${beautifyNumber(Number(r.total) || 0, 0, true)}`);

export const uploadsBreakdownHeaders = (t) => [
    t('UploadMonth'), t('Expenses'), t('Incomes'), t('PaymentAcceptances'),
    t('OtherDocuments'), t('BillableUploads'), t('ProcessedPages'),
];

/** Newest month first, matching how the modal shows it. */
const tableRows = (data) =>
    [...(data?.months || [])].reverse().map((m) => [formatUploadMonth(m), ...COUNT_KEYS.map((k) => Number(m[k]) || 0)]);

const totalRow = (data, t) => [t('Total'), ...COUNT_KEYS.map((k) => Number(data?.totals?.[k]) || 0)];

const hasLegacyMonths = (data) => (data?.months || []).some((m) => m.fromUsageTable === false);

const noteLines = (data, t) => [t('UploadsBreakdownNote'), ...(hasLegacyMonths(data) ? [t('UploadsBreakdownLegacyNote')] : [])];

const newestFirst = (data) => [...(data?.months || [])].reverse();

export const downloadUploadsBreakdownExcel = ({ data, clientName, clientCase, t }) => {
    const header = [...uploadsBreakdownHeaders(t), t('SavedInReportingMonths')];
    const monthLines = newestFirst(data).map(splitLines);
    const rows = [
        ...tableRows(data).map((row, i) => [...row, monthLines[i].join('\n') || '—']),
        [...totalRow(data, t), ''],
    ];
    const titleLines = [`${clientCase || ''}    ${clientName || ''}`.trim(), t('UploadsBreakdownTitle')];
    const wb = buildReportWorkbook({
        sheetName: 'Uploads',
        titleLines,
        header,
        rows,
        colWidths: [16, 12, 12, 18, 16, 16, 16, 24],
        totalRowIndexes: [rows.length - 1],
    });

    const ws = wb.Sheets[wb.SheetNames[0]];
    const firstDataRow = titleLines.length + 1 + 1;
    const splitCol = header.length - 1;
    monthLines.forEach((lines, i) => {
        const cell = ws[XLSX.utils.encode_cell({ r: firstDataRow + i, c: splitCol })];
        if (cell) cell.s = { ...cell.s, alignment: { ...cell.s?.alignment, wrapText: true } };
        ws['!rows'][firstDataRow + i] = { hpt: Math.max(18, lines.length * 15 + 4) };
    });

    // Notes are long sentences: one merged, wrapped row each below the table.
    const firstNoteRow = firstDataRow + rows.length + 1;
    const notes = [...noteLines(data, t), t('SavedInReportingMonthsNote')];
    XLSX.utils.sheet_add_aoa(ws, notes.map((line) => [line]), { origin: { r: firstNoteRow, c: 0 } });
    notes.forEach((_, i) => {
        const r = firstNoteRow + i;
        ws['!merges'].push({ s: { r, c: 0 }, e: { r, c: header.length - 1 } });
        ws[XLSX.utils.encode_cell({ r, c: 0 })].s = {
            font: { sz: 10, color: { rgb: 'FF4B5563' } },
            alignment: { horizontal: 'right', vertical: 'top', wrapText: true },
        };
        ws['!rows'][r] = { hpt: 44 };
    });

    const split = splitRows(data);
    if (split.length > 0) {
        const splitWb = buildReportWorkbook({
            titleLines: [titleLines[0], t('UploadsByReportingMonthTitle')],
            header: splitHeaders(t),
            rows: split,
            colWidths: [16, 16, 12, 12, 18, 16, 12],
        });
        XLSX.utils.book_append_sheet(wb, splitWb.Sheets[splitWb.SheetNames[0]], 'By reporting month');
    }
    XLSX.writeFile(wb, `${safeFileName(clientName)}-${clientCase}_Uploads.xlsx`);
};

const assetToDataUrl = async (assetUrl) => {
    try {
        const res = await fetch(assetUrl);
        if (!res.ok) return '';
        const blob = await res.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result || ''));
            reader.onerror = () => resolve('');
            reader.readAsDataURL(blob);
        });
    } catch {
        return '';
    }
};

const buildPdfHtml = ({ data, clientName, clientCase, logoDataUrl, t }) => {
    const cell = (v, i) => (i === 0
        ? `<td class="txt">${escapeHtml(v)}</td>`
        : `<td class="num">${escapeHtml(beautifyNumber(v, 0, true))}</td>`);
    const monthLines = newestFirst(data).map(splitLines);
    const splitCellHtml = (lines) => `<td class="split">${lines.length ? lines.map(escapeHtml).join('<br/>') : '—'}</td>`;
    const body = tableRows(data)
        .map((row, idx) => `<tr class="${idx % 2 === 1 ? 'stripe' : 'plain'}">${row.map(cell).join('')}${splitCellHtml(monthLines[idx])}</tr>`)
        .join('');
    const split = splitRows(data);
    const splitCell = (v, i) => (i < 2
        ? `<td class="txt">${escapeHtml(v)}</td>`
        : `<td class="num">${escapeHtml(beautifyNumber(v, 0, true))}</td>`);
    const splitHtml = split.length === 0 ? '' : `
  <h2>${escapeHtml(t('UploadsByReportingMonthTitle'))}</h2>
  <table>
    <thead><tr>${splitHeaders(t).map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead>
    <tbody>${split.map((row, idx) => `<tr class="${idx % 2 === 1 ? 'stripe' : 'plain'}">${row.map(splitCell).join('')}</tr>`).join('')}</tbody>
  </table>
  <div class="note">${escapeHtml(t('SavedInReportingMonthsNote'))}</div>`;
    const now = new Date();
    const printedAt = `${now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })} ${now.toLocaleDateString('he-IL')}`;

    return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(t('UploadsBreakdownTitle'))}</title>
  <style>
    @page { size: A4 portrait; margin: 10mm 8mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, "Segoe UI", sans-serif; direction: rtl; color: ${C.text}; margin: 0; padding: 6px;
      -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .top-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; font-size: 11px; }
    .top-bar img { height: 28px; }
    .center-block { text-align: center; flex: 1; }
    h1 { margin: 2px 0 4px; font-size: 18px; color: ${C.primary}; font-weight: 700; }
    .meta { font-size: 11px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 10px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    th, td { border: 1px solid ${C.border}; padding: 5px 4px; text-align: center; }
    th { background: ${C.primary} !important; color: ${C.headerFont} !important; font-weight: 700; }
    td.split { direction: ltr; line-height: 1.5; font-weight: 600; }
    td.num { white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 600; }
    tr.plain td { background: ${C.plain} !important; }
    tr.stripe td { background: ${C.stripe} !important; }
    tr.total td { font-weight: 700; background: ${C.totalFill} !important; color: ${C.primaryDark} !important; }
    h2 { margin: 18px 0 6px; font-size: 13px; color: ${C.primaryDark}; }
    .note { font-size: 10px; line-height: 1.5; margin-top: 10px; color: #4B5563; }
  </style>
</head>
<body>
  <div class="top-bar">
    <div>${logoDataUrl ? `<img src="${logoDataUrl}" alt="Xtram" />` : ''}</div>
    <div class="center-block">
      <div class="meta">${escapeHtml(`${clientName || ''} ${clientCase ? `(${clientCase})` : ''}`.trim())}</div>
      <h1>${escapeHtml(t('UploadsBreakdownTitle'))}</h1>
    </div>
    <div>${escapeHtml(printedAt)}</div>
  </div>
  <table>
    <thead><tr>${[...uploadsBreakdownHeaders(t), t('SavedInReportingMonths')].map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead>
    <tbody>${body}<tr class="total">${totalRow(data, t).map(cell).join('')}<td></td></tr></tbody>
  </table>
  ${noteLines(data, t).map((line) => `<div class="note">${escapeHtml(line)}</div>`).join('')}
  ${splitHtml}
</body>
</html>`;
};

export const downloadUploadsBreakdownPdf = async ({ data, clientName, clientCase, token, t }) => {
    if (!token) throw new Error('Missing auth token');
    if (!clientCase) throw new Error('Missing case id');

    const logoDataUrl = await assetToDataUrl(XTRAM_LOGOS.wordmarkColorPng);
    const html = buildPdfHtml({ data, clientName, clientCase, logoDataUrl, t });
    const res = await EnumsService.GeneratePdfFromHtml(html, clientCase, token);
    if (!res?.ok) {
        const errText = await res?.text?.().catch(() => '');
        throw new Error(errText || 'PDF generation failed');
    }

    let blob;
    if ((res.headers.get('content-type') || '').includes('application/json')) {
        const json = await res.json();
        const pdfBase64 = json.data?.PdfBase64 || json.data?.pdfBase64 || json.PdfBase64;
        if (!pdfBase64) throw new Error('No PDF base64 in response');
        const binary = atob(pdfBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        blob = new Blob([bytes], { type: 'application/pdf' });
    } else {
        blob = await res.blob();
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeFileName(clientName)}-${clientCase}_Uploads.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
